import { FC, PropsWithChildren } from "react";
import { useTranslation } from "react-i18next";

import {
  Box,
  Text,
  Title,
  Loader,
  Container,
  createStyles,
} from "@mantine/core";
import { BiSave } from "react-icons/bi";

import { Header } from "@components/Header/Header";

const useStyles = createStyles((t) => ({
  containerWrapper: {
    flex: 1,
    display: "flex",
    overflow: "auto",
    padding: t.spacing.lg,
    paddingTop: 70,
    flexDirection: "column",
  },
  container: {
    flex: 1,
    width: "100%",
  },
  saveIndicator: {
    display: "flex",
    gap: t.spacing.xs,
    alignItems: "center",
  },
  formWrapper: {
    width: "100%",
    maxWidth: 600,
    marginInline: "auto",
  },
  formTitle: {
    textAlign: "center",
  },
  formDescription: {
    marginBottom: 64,
    textAlign: "center",
    marginTop: t.spacing.md,
  },
  currentStep: {
    fontWeight: "bold",
    textAlign: "center",
    color: t.primaryColor,
  },
}));

export const Form: FC<
  {
    title: string;
    isSaving: boolean;
    totalSteps: number;
    description: string;
    currentStep: number;
    className?: string;
    showSteps?: boolean;
    showHeader?: boolean;
    showDescription?: boolean;
  } & PropsWithChildren
> = ({
  currentStep,
  description,
  isSaving,
  title,
  totalSteps,
  children,
  className = "",
  showSteps = true,
  showHeader = true,
  showDescription = true,
}) => {
  const { t } = useTranslation();
  const { classes } = useStyles();
  return (
    <>
      {showHeader && (
        <Header
          rightSection={
            <Box className={classes.saveIndicator}>
              {isSaving ? <Loader size="xs" /> : <BiSave />}

              <Text color="dimmed" size="xs">
                {isSaving ? `${t("Saving")}...` : t("Saved")}
              </Text>
            </Box>
          }
        />
      )}
      <Box className={`${classes.containerWrapper} ${className}`}>
        <Container
          className={classes.container}
          style={{ paddingTop: "unset" }}
        >
          <Box className={classes.formWrapper}>
            {showSteps && (
              <Box className={classes.currentStep}>
                {t("Step {{currentStep}} of {{totalSteps}}", {
                  totalSteps,
                  currentStep,
                })}
              </Box>
            )}
            <Title order={2} className={classes.formTitle}>
              {t(title)}
            </Title>
            <Text className={classes.formDescription} color="dimmed" size="sm">
              {showDescription && t(description)}
            </Text>
            {children}
          </Box>
        </Container>
      </Box>
    </>
  );
};
